import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaEdit, FaPlus, FaTimes, FaTrash, FaUsers } from "react-icons/fa";
import {
  deleteAgent,
  editAgent,
  fetchProjectAgents,
  fetchProjectForTeams,
} from "../../api/teams.api";
import { apihubGet } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import StatusCapsule from "../StatusCapsule";

const PAGE_SIZES = [100, 200, 500, 1000];

const EMPTY_EDIT = {
  fullName: "",
  email: "",
  mobile: "",
  password: "",
  address: "",
  agentid: "",
  teamRole: null,
  extractedalphaid: "",
  extractedalphaprojectid: "",
};

const AllTeams = () => {
  const { projectId } = useProjectPhone();
  const [agents, setAgents] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [maxAgents, setMaxAgents] = useState(null);
  const [teamCount, setTeamCount] = useState(0);
  const [adminId, setAdminId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(1);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [agentRes, projectRes, profileRes] = await Promise.all([
        fetchProjectAgents(projectId),
        fetchProjectForTeams(projectId),
        apihubGet("/getInteractedCustomerDetail/profile").catch(() => null),
      ]);

      const list = agentRes?.data?.teams || agentRes?.teams || [];
      setAgents(Array.isArray(list) ? list : []);

      const project = projectRes?.project || projectRes?.data?.project || {};
      setTeamRoles(project.teamroles || []);
      setTeamCount(Array.isArray(project.teams) ? project.teams.length : 0);
      setMaxAgents(
        typeof project.noOfAgents === "number" ? project.noOfAgents : null,
      );

      const user = profileRes?.user || profileRes;
      const matched = user?.matchedUser || user;
      if (matched?._id) setAdminId(matched._id);
    } catch (err) {
      setAgents([]);
      setError(err.message || "Failed to load agents");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(agents.length / pageSize));
  const pagedAgents = useMemo(() => {
    const start = (page - 1) * pageSize;
    return agents.slice(start, start + pageSize);
  }, [agents, page, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const openEdit = (agent) => {
    setEditForm({
      fullName: agent.user_name || agent.display_name || "",
      email: agent.email || "",
      mobile: agent.contact || "",
      password: "",
      address: agent.address || "",
      agentid: agent._id,
      teamRole: agent.teamroles?.[0] || null,
      extractedalphaid: adminId,
      extractedalphaprojectid: projectId,
    });
    setEditOpen(true);
    setError("");
    setMessage("");
  };

  const onEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const onEditRoleChange = (e) => {
    const role = teamRoles.find((r) => r._id === e.target.value) || null;
    setEditForm((prev) => ({ ...prev, teamRole: role }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (maxAgents != null && teamCount > maxAgents) {
      setError("Please upgrade your plan to add more agents.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await editAgent(editForm);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to update agent");
      }
      setMessage(res?.message || "Team updated successfully!");
      setEditOpen(false);
      load();
    } catch (err) {
      setError(err.message || "Failed to update agent");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (agentId) => {
    if (!window.confirm("Delete this team member?")) return;
    setError("");
    setMessage("");
    try {
      const res = await deleteAgent(agentId);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to delete agent");
      }
      setMessage(res?.message || "Team member deleted successfully.");
      load();
    } catch (err) {
      setError(err.message || "Failed to delete agent");
    }
  };

  return (
    <div className="teams-page">
      <header className="teams-header teams-header-row">
        <div>
          <h1>
            <FaUsers aria-hidden />
            All Agents
          </h1>
          <p>
            Distribute conversations across agents to ensure quicker responses
            and better customer experience.
          </p>
        </div>
        <Link to={businessPath(projectId, "create-teams")} className="teams-add-btn">
          <FaPlus aria-hidden />
          Add Team Member
        </Link>
      </header>

      {error && !editOpen && (
        <p className="teams-alert teams-alert-error">{error}</p>
      )}
      {message && (
        <p className="teams-alert teams-alert-success">{message}</p>
      )}

      <section className="teams-table-section">
        <div className="teams-table-wrap">
          <table className="teams-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Team Role</th>
                <th>Status</th>
                <th className="biz-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="teams-table-empty">
                    Loading agents…
                  </td>
                </tr>
              ) : pagedAgents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="teams-table-empty">
                    No agents available.
                  </td>
                </tr>
              ) : (
                pagedAgents.map((agent, idx) => (
                  <tr key={agent._id || idx}>
                    <td>{(page - 1) * pageSize + idx + 1}</td>
                    <td>{agent.user_name || agent.display_name || "—"}</td>
                    <td>{agent.email || "—"}</td>
                    <td>{agent.contact || "—"}</td>
                    <td>{agent.teamroles?.[0]?.name || "—"}</td>
                    <td>
                      <StatusCapsule
                        value={
                          agent.isloggedIn || agent.is_logged_in
                            ? "Online"
                            : "Offline"
                        }
                      />
                    </td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions teams-row-actions">
                        <button
                          type="button"
                          className="teams-action-btn teams-action-edit"
                          onClick={() => openEdit(agent)}
                          aria-label="Edit agent"
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="teams-action-btn teams-action-delete"
                          onClick={() => remove(agent._id)}
                          aria-label="Delete agent"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && agents.length > 0 && (
          <div className="teams-pagination">
            <label>
              <span>Show:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </label>
            <div className="teams-pagination-nav">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      {editOpen && (
        <div className="teams-modal-backdrop">
          <div className="teams-modal-card" role="dialog" aria-labelledby="edit-team-title">
            <div className="teams-modal-head">
              <h3 id="edit-team-title">Edit the Team</h3>
              <button
                type="button"
                className="teams-modal-close"
                onClick={() => setEditOpen(false)}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            {error && <p className="teams-alert teams-alert-error">{error}</p>}

            <form className="teams-form" onSubmit={saveEdit}>
              <div className="teams-form-grid teams-form-grid-3">
                <label className="teams-field">
                  <span>Full Name *</span>
                  <input
                    type="text"
                    name="fullName"
                    value={editForm.fullName}
                    onChange={onEditChange}
                    required
                  />
                </label>
                <label className="teams-field">
                  <span>Email *</span>
                  <input
                    type="email"
                    name="email"
                    value={editForm.email}
                    onChange={onEditChange}
                    required
                  />
                </label>
                <label className="teams-field">
                  <span>Mobile *</span>
                  <input
                    type="text"
                    name="mobile"
                    value={editForm.mobile}
                    onChange={onEditChange}
                    required
                  />
                </label>
              </div>

              <label className="teams-field">
                <span>Password *</span>
                <div className="teams-password-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={editForm.password}
                    onChange={onEditChange}
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    className="teams-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </label>

              <label className="teams-field">
                <span>Address</span>
                <textarea
                  name="address"
                  value={editForm.address}
                  onChange={onEditChange}
                  rows={3}
                />
              </label>

              <label className="teams-field teams-field-half">
                <span>Team Role *</span>
                <select
                  value={editForm.teamRole?._id || ""}
                  onChange={onEditRoleChange}
                  required
                >
                  <option value="">Select</option>
                  {teamRoles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </label>

              <button type="submit" className="teams-submit-btn" disabled={saving}>
                {saving ? "Updating…" : "Update"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllTeams;
